import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  CircularProgress,
  Chip,
  GlobalStyles,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email,
  Security,
  CheckCircle,
  ArrowBack,
  VerifiedUser,
  FiberManualRecord,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';

// ─── Fixed Light Theme ──────────────────────────────────────────────────────
const palette = {
  ink: '#0B1220',
  inkSoft: '#2A3342',
  muted: '#5B6472',
  faint: '#8A93A3',
  paper: '#FBFAF7',
  card: '#FFFFFF',
  border: '#E7E3D8',
  ledger: '#0E1B2E',
  ledgerLine: 'rgba(255,255,255,0.08)',
  teal: '#0F766E',
  tealDark: '#0B5C56',
  tealSoft: 'rgba(15,118,110,0.10)',
  amber: '#D97706',
  red: '#DC2626',
  green: '#16A34A',
};

const lightOnlyTheme = createTheme({
  palette: {
    mode: 'light',
    background: { default: palette.paper, paper: palette.card },
    text: { primary: palette.ink, secondary: palette.muted },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
});

export default function ForgotPassword() {
  const isMobile = useMediaQuery('(max-width:960px)');
  const navigate = useNavigate();

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '1rem',
      color: palette.ink,
      bgcolor: palette.card,
      '& input': { py: 1.85 },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${palette.card} inset`,
        WebkitTextFillColor: palette.ink,
      },
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: '#C7C1B0' },
      '&.Mui-focused fieldset': { borderColor: palette.teal, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: palette.muted, fontSize: '0.95rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: palette.tealDark },
  };

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/v1/password/forgot', { email });
      setSuccess(true);
      setEmail('');
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={lightOnlyTheme}>
      <GlobalStyles
        styles={{
          '@import':
            "url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap')",
          ':root': { colorScheme: 'light' },
          html: { colorScheme: 'light' },
          body: { colorScheme: 'light', backgroundColor: palette.paper },
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          bgcolor: palette.paper,
          colorScheme: 'light',
        }}
      >
        {/* ─── LEFT PANEL — "Live Ledger" ──────────────────────────────────── */}
        {!isMobile && (
          <Box
            sx={{
              flex: '1 1 52%',
              maxWidth: '52%',
              bgcolor: palette.ledger,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              pt: { md: 8, lg: 10 },
              px: { md: 6, lg: 9 },
              pb: 6,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                maskImage: 'radial-gradient(ellipse 75% 55% at 25% 15%, black, transparent)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -180,
                left: -140,
                width: 480,
                height: 480,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${palette.tealSoft} 0%, transparent 70%)`,
                filter: 'blur(6px)',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(15,118,110,0.16)',
                  border: '1px solid rgba(94,234,212,0.25)',
                  borderRadius: '999px',
                  px: 1.6,
                  py: 0.5,
                  mb: 4.5,
                }}
              >
                <FiberManualRecord sx={{ fontSize: 8, color: '#5EEAD4' }} />
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11.5,
                    letterSpacing: '0.08em',
                    color: '#99F6E4',
                    fontWeight: 500,
                  }}
                >
                  LIVE VENDOR RISK LEDGER
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: '#F5F4F0',
                  fontSize: { md: 33, lg: 40 },
                  lineHeight: 1.16,
                  letterSpacing: '-0.01em',
                  mb: 2.5,
                }}
              >
                Reset Your Password
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(226,232,240,0.72)',
                  fontSize: 16,
                  lineHeight: 1.7,
                  mb: 5,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Enter your email address and we'll send you a secure link to reset your password.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <Security sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    Secure Reset
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <VerifiedUser sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    24 Hours Valid
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ─── RIGHT PANEL — Forgot Password Form ─────────────────────────── */}
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 48%' },
            maxWidth: { xs: '100%', md: '48%' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 5 },
            bgcolor: palette.paper,
            minHeight: '100vh',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 480,
              p: 0,
              borderRadius: 3,
              bgcolor: 'transparent',
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', mb: isMobile ? 5 : 6 }}>
              <img
                src="/logo.png"
                alt="Platform logo"
                style={{ height: isMobile ? 84 : 108, width: 'auto' }}
              />
            </Box>

            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: palette.ink,
                  fontSize: 30,
                  letterSpacing: '-0.01em',
                  mb: 1,
                }}
              >
                Forgot Password?
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: 15.5 }}>
                No worries! We'll send you a password reset link.
              </Typography>
            </Box>

            {success ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#ECFDF3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 36, color: palette.green }} />
                </Box>

                <Typography
                  sx={{
                    fontFamily: "'Fraunces', serif",
                    fontWeight: 500,
                    color: palette.ink,
                    fontSize: 24,
                    mb: 1,
                  }}
                >
                  Check Your Email
                </Typography>

                <Typography sx={{ color: palette.muted, mb: 3, fontSize: 15 }}>
                  If an account exists with this email, you will receive a password reset link.
                  The link will expire in 24 hours.
                </Typography>

                <Typography sx={{ color: palette.muted, mb: 3, fontSize: 13 }}>
                  Didn't receive the email? Check your spam folder or{' '}
                  <Link
                    component="button"
                    onClick={() => {
                      setSuccess(false);
                    }}
                    sx={{ color: palette.tealDark, cursor: 'pointer', fontWeight: 600 }}
                  >
                    try again
                  </Link>
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => navigate('/login')}
                  disableElevation
                  sx={{
                    py: 1.7,
                    px: 4,
                    borderRadius: '8px',
                    bgcolor: palette.teal,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: palette.tealDark, boxShadow: 'none' },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 15.5,
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography
                  component="label"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  required
                  sx={{ mb: 3, ...inputSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  disableElevation
                  sx={{
                    py: 1.7,
                    borderRadius: '8px',
                    bgcolor: palette.teal,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: palette.tealDark, boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#D7D2C4', color: '#fff' },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 15.5,
                  }}
                >
                  {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send Reset Link'}
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    startIcon={<ArrowBack />}
                    sx={{
                      color: palette.muted,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'transparent' },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </form>
            )}
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}